import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";

const ContactPartnerModal = ({ partner, setShow, backendUrl, token }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [subject, setSubject] = useState("");
  const [emailContent, setEmailContent] = useState("");
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [availableEmails, setAvailableEmails] = useState([]);

  useEffect(() => {
    // Initialize available emails and default selections
    const emails = [];
    if (partner?.company_email) {
      emails.push({
        email: partner.company_email,
        type: "Company Email",
        selected: true,
      });
    }
    if (partner?.personal_email) {
      let selected =
        partner?.personal_email.toLowerCase() !==
        partner?.company_email.toLowerCase();
      emails.push({
        email: partner.personal_email,
        type: "Personal Email",
        selected,
      });
    }

    setAvailableEmails(emails);
    setSelectedEmails(emails.filter((e) => e.selected).map((e) => e.email));
  }, [partner]);

  const handleClose = () => {
    setShow(false);
  };

  const handleEmailSelection = (email) => {
    setAvailableEmails((prevEmails) =>
      prevEmails.map((e) =>
        e.email === email ? { ...e, selected: !e.selected } : e
      )
    );

    setSelectedEmails((prevSelected) => {
      if (prevSelected.includes(email)) {
        return prevSelected.filter((e) => e !== email);
      } else {
        return [...prevSelected, email];
      }
    });
  };

  const handleSendEmail = async () => {
    if (!subject.trim()) {
      toast.error("Please enter a subject");
      return;
    }

    if (!emailContent.trim() || emailContent === "<p><br></p>") {
      toast.error("Please enter email content");
      return;
    }

    if (selectedEmails.length === 0) {
      toast.error("Please select at least one recipient email");
      return;
    }

    try {
      setIsLoading(true);
      const res = await fetch(`${backendUrl}/api/partnership/contact-partner`, {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          partnerId: partner.id,
          subject,
          message: emailContent,
          recipientEmails: selectedEmails,
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to send email");
      }

      const data = await res.json();
      toast.success(data.message || "Email sent successfully");
      handleClose();
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error(error.message || "Failed to send email");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal show={true} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Contact Partner: {partner?.company_name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Recipients</Form.Label>
            {availableEmails.length > 0 ? (
              <div className="mb-2 d-flex gap-3">
                {availableEmails.map((emailObj, index) => (
                  <Form.Check
                    key={index}
                    type="checkbox"
                    id={`email-${index}`}
                    label={`${emailObj.type}: ${emailObj.email}`}
                    checked={emailObj.selected}
                    onChange={() => handleEmailSelection(emailObj.email)}
                    className="mb-1"
                  />
                ))}
              </div>
            ) : (
              <p className="text-danger">
                No email addresses available for this partner
              </p>
            )}

            <div className="mt-2">
              <Form.Label>Selected Recipients:</Form.Label>
              {selectedEmails.length > 0 ? (
                <div className="d-flex flex-wrap gap-2">
                  {selectedEmails.map((email, index) => (
                    <div key={index} className="badge bg-primary p-2">
                      {email}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-danger"> No email selected </p>
              )}
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Subject</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter email subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Message</Form.Label>
            <div style={{ height: "300px" }}>
              <ReactQuill
                theme="snow"
                className="descriptions"
                style={{ height: "250px" }}
                value={emailContent}
                onChange={setEmailContent}
                modules={{
                  toolbar: [
                    [{ header: [1, 2, 3, false] }],
                    ["bold", "italic", "underline", "strike"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    [{ color: [] }, { background: [] }],
                    ["link"],
                    ["clean"],
                  ],
                }}
              />
            </div>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSendEmail}
          disabled={isLoading || selectedEmails.length === 0}
        >
          {isLoading
            ? "Sending..."
            : `Send Email to ${selectedEmails.length} recipient${
                selectedEmails.length !== 1 ? "s" : ""
              }`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ContactPartnerModal;
